/**
 * BulkRecomputeLocationAction — bulk "Przelicz lokalizację" action for the
 * media-units catalog list.
 *
 * Reuses the bulk-edit-media selection pattern verbatim: operates on the
 * caller's explicit checkbox selection (selectedIds) when present, otherwise
 * the active "select all N matching" filter (selectAllFilter) — mirrors
 * `BulkEditSelectionSchema`'s `{ ids } | { filter }` union and the same
 * 500-row `BULK_EDIT_CAP` as bulk-edit.
 *
 * The "Przelicz lokalizację" trigger lives in the shared floating
 * `BulkActionToolbar` (rendered by the route); this component owns only the
 * confirm dialog, controlled by the caller's `isOpen`/`onOpenChange` props.
 * Województwo, powiat, and aglomeracja are not free-text editable in
 * bulk-edit — they are re-derived server-side from each unit's coordinates.
 *
 * This component stays permanently mounted in the route tree regardless of
 * `isOpen` (only its rendered output is gated), so the route remounts it via
 * a changing `key` on every open to guarantee a fresh `serverError` — an
 * effect-based reset would flash the previous session's stale error for one
 * paint before clearing it.
 *
 * Owns the `useBulkRecomputeLocationMutation` call and the updated/failed
 * result toast. On request failure the dialog stays open with an inline
 * server error.
 *
 * Traceability: bulk-edit-media / Requirement: Bulk "recompute location from
 * coordinates" action.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { parseApiError } from '@ooh/contracts';
import type { MediaUnitFilters } from '@ooh/contracts';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import { useBulkRecomputeLocationMutation } from '../api/mutations';
import { isBulkSelectionCapExceeded, shouldShowHiddenCount } from '../lib/bulk-selection-cap';

export interface BulkRecomputeLocationActionProps {
  /** Holder of bulk_edit — renders the dialog. */
  canBulkEdit: boolean;
  /** Controlled open state, driven by the shared BulkActionToolbar's "Przelicz lokalizację" action. */
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Explicit checkbox selection (useMediaBulkSelection.selectedIds). */
  selectedIds: number[];
  /** Active "Select all N matching" filter, when the user activated that mode. */
  selectAllFilter: MediaUnitFilters | null;
  /** Total rows matching the current filter (for the confirm count + cap check). */
  totalMatching: number;
  /**
   * Of `selectedIds`, how many are not in the current results (id-based
   * selection only — a filter-based selection has no concept of "hidden").
   */
  hiddenCount?: number;
  /** Called after a successful recompute so the caller can clear its selection. */
  onSuccess?: () => void;
}

export function BulkRecomputeLocationAction({
  canBulkEdit,
  isOpen,
  onOpenChange,
  selectedIds,
  selectAllFilter,
  totalMatching,
  hiddenCount = 0,
  onSuccess,
}: BulkRecomputeLocationActionProps) {
  const { t } = useTranslation('inventory');
  const mutation = useBulkRecomputeLocationMutation();
  const [serverError, setServerError] = React.useState<string | null>(null);

  if (!canBulkEdit) return null;

  const isFilterSelection = selectAllFilter != null;
  const effectiveCount = isFilterSelection ? totalMatching : selectedIds.length;
  const capExceeded = isBulkSelectionCapExceeded(
    isFilterSelection,
    totalMatching,
    selectedIds.length,
  );

  async function handleSubmit() {
    if (capExceeded) return;
    setServerError(null);
    try {
      const response = await mutation.mutateAsync({
        selection:
          isFilterSelection && selectAllFilter != null
            ? { filter: selectAllFilter }
            : { ids: selectedIds },
      });
      toast.success(
        t('bulkRecomputeLocation.resultToast', {
          updated: response.updated,
          failed: response.failed,
        }),
      );
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      setServerError(parseApiError(error).message);
    }
  }

  if (!isOpen) return null;

  return (
    <ModalDialog
      header={
        <span className="text-base font-semibold">{t('bulkRecomputeLocation.dialogTitle')}</span>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid="bulk-recompute-location-cancel-btn"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('bulkRecomputeLocation.cancel')}
          </Button>
          <Button
            size="sm"
            data-testid="bulk-recompute-location-submit-btn"
            onClick={() => void handleSubmit()}
            disabled={capExceeded || mutation.isPending}
          >
            {mutation.isPending ? '...' : t('bulkRecomputeLocation.submit')}
          </Button>
        </div>
      }
      onClose={() => onOpenChange(false)}
    >
      <div className="space-y-4">
        <div
          className="rounded-lg border border-border bg-muted/20 px-4 py-3 text-sm"
          data-testid="bulk-recompute-location-count"
        >
          {t('bulkRecomputeLocation.confirmMessage', { count: effectiveCount })}
          {shouldShowHiddenCount(isFilterSelection, hiddenCount) ? (
            <div data-testid="bulk-recompute-location-hidden-count">
              {t('bulkRecomputeLocation.hiddenCount', { count: hiddenCount })}
            </div>
          ) : null}
        </div>
        {capExceeded ? (
          <div
            className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            data-testid="bulk-recompute-location-cap-warning"
          >
            {t('bulkRecomputeLocation.capWarning')}
          </div>
        ) : null}
        {serverError ? (
          <div
            className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            data-testid="bulk-recompute-location-server-error"
          >
            {serverError}
          </div>
        ) : null}
      </div>
    </ModalDialog>
  );
}
